export const DEFAULT_REQUEST_TIMEOUT_MS = 12_000;

export function isTimeoutError(error: unknown): boolean {
  if (!(error instanceof Error)) return false;
  if (error.name === "TimeoutError" || error.name === "AbortError") return true;
  const message = error.message.toLowerCase();
  return message.includes("aborted due to timeout") || message.includes("operation was aborted");
}

export interface FetchWithRetryOptions {
  timeoutMs?: number;
  retries?: number;
}

/**
 * Fetch with a per-attempt timeout that retries once on transient timeout errors.
 * Used by the doctor's auth/workspace checks where serverless cold starts can
 * push a single request past a tight 7s budget even when the service is healthy.
 */
export async function fetchWithRetry(
  url: string,
  init: Omit<RequestInit, "signal">,
  options: FetchWithRetryOptions = {},
): Promise<Response> {
  const timeoutMs = options.timeoutMs ?? DEFAULT_REQUEST_TIMEOUT_MS;
  const retries = options.retries ?? 1;

  let lastError: unknown;
  for (let attempt = 0; attempt <= retries; attempt++) {
    try {
      return await fetch(url, { ...init, signal: AbortSignal.timeout(timeoutMs) });
    } catch (error) {
      lastError = error;
      if (!isTimeoutError(error) || attempt === retries) {
        throw error;
      }
    }
  }

  throw lastError;
}
